// src/components/NewCounterModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Textarea,
  useToast,
  Spinner,
  Select,
} from "@chakra-ui/react";
import { usePos } from "../../context/PosContext.js";
import axios from "axios";
import { BASE_URL } from "../../services/api.js";


export default function NewCounterModal({ isOpen, onClose }) {
  const { createCounter } = usePos();
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: "",
    store: "",
    assignedToUserId: "",
    isActive: true,
    metadata: {
      deviceType: "",
      ipAddress: "",
      location: "",
    },
  });

  const [loadingMeta, setLoadingMeta] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Auto-capture device type & IP
  useEffect(() => {
    if (!isOpen) return;

    const fetchMeta = async () => {
      setLoadingMeta(true);
      try {
        const deviceType = navigator.userAgent;

        const res = await fetch("https://api.ipify.org?format=json");
        const { ip } = await res.json();

        setFormData((prev) => ({
          ...prev,
          metadata: { ...prev.metadata, deviceType, ipAddress: ip },
        }));
      } catch (err) {
        console.error("Failed to fetch metadata:", err);
      } finally {
        setLoadingMeta(false);
      }
    };

    fetchMeta();
  }, [isOpen]);

  // Fetch users for dropdown
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/novatraAuthRoutes/getUsers?tenantId=68c68cc1690c76521da83c75`
        );
        setUsers(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["location"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleToggle = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleSubmit = async () => {
    try {
      await createCounter(formData);
      toast({
        title: "Counter created.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      toast({
        title: "Error creating counter.",
        description: err.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New POS Counter</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3} isRequired>
            <FormLabel>Name</FormLabel>
            <Input name="name" value={formData.name} onChange={handleChange} />
          </FormControl>

          <FormControl mb={3} isRequired>
            <FormLabel>Store</FormLabel>
            <Input
              name="store"
              value={formData.store}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Assign to User</FormLabel>
            {loadingUsers ? (
              <Spinner size="sm" />
            ) : (
              <Select
                placeholder="Select user"
                value={formData.assignedToUserId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    assignedToUserId: e.target.value,
                  }))
                }
              >
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </Select>
            )}
          </FormControl>

          <FormControl display="flex" alignItems="center" mb={3}>
            <FormLabel mb="0">Active</FormLabel>
            <Switch
              isChecked={formData.isActive}
              onChange={handleToggle}
              colorScheme="teal"
            />
          </FormControl>

          {/* Metadata auto-filled */}
          <FormControl mb={3}>
            <FormLabel>Device Type</FormLabel>
            {loadingMeta ? (
              <Spinner size="sm" />
            ) : (
              <Input
                name="deviceType"
                value={formData.metadata.deviceType}
                isReadOnly
              />
            )}
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>IP Address</FormLabel>
            {loadingMeta ? (
              <Spinner size="sm" />
            ) : (
              <Input
                name="ipAddress"
                value={formData.metadata.ipAddress}
                isReadOnly
              />
            )}
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Location (optional)</FormLabel>
            <Textarea
              name="location"
              value={formData.metadata.location}
              onChange={handleChange}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="teal" onClick={handleSubmit}>
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
